import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Store, UtensilsCrossed } from "lucide-react";
import { fadeUp, stagger, EASE } from "@/lib/motion";

const IMG_HYPER =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/2024-05-28_Aisle_4%2C_Asda%2C_Elgin.jpg/1280px-2024-05-28_Aisle_4%2C_Asda%2C_Elgin.jpg";
const IMG_VEG =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1d/Vegetables_in_Sainsbury%27s%2C_Alma_Place%2C_Newquay%2C_Cornwall_-_May_2025.jpg/1280px-Vegetables_in_Sainsbury%27s%2C_Alma_Place%2C_Newquay%2C_Cornwall_-_May_2025.jpg";
const IMG_SELFCHECKOUT =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/Basket_self_checkout_in_Sainsbury%27s_Weedon_Road_Superstore%2C_Northampton_%2831052015587%29.jpg/1280px-Basket_self_checkout_in_Sainsbury%27s_Weedon_Road_Superstore%2C_Northampton_%2831052015587%29.jpg";
const IMG_DETERGENT =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/3/36/20200926_172032_Detergents.jpg/1280px-20200926_172032_Detergents.jpg";
const IMG_HORECA =
  "https://images.unsplash.com/photo-1581349485608-9469926a8e5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXN0YXVyYW50JTIwY2hlZiUyMHBsYXRpbmclMjBmb29kJTIwaG9zcGl0YWxpdHklMjBob3JlY2F8ZW58MHx8fHwxNzg4NDM0Mzk0fDA&ixlib=rb-4.1.0&q=85";

const SECTORS = {
  supermarkets: {
    label: "Supermarkets & Hypermarkets",
    icon: Store,
    images: {
      food: ["/images/aisle-canned.jpg", "/images/aisle-soup.jpg"],
      nonfood: ["/images/nonfood-detergents.jpg"],
    },
    alt: "UK supermarket shelves stocked with canned goods, snacks and pound sterling pricing",
    desc: "Category leadership for grocery networks — from discount pressure to premium own-label ambition.",
    food: [
      "Fresh & chilled category resets engineered for velocity",
      "Private label tiers that defend margin against discounters",
      "Frozen & ambient planograms tuned to basket economics",
      "Supplier dual-sourcing across 18 European markets",
    ],
    nonfood: [
      "Household & personal care GMROII uplift programmes",
      "Eco-packaging transitions ahead of EU regulation",
      "Seasonal non-food buying calendars & markdown shields",
      "Listing-fee and rebate architecture for A-brands",
    ],
  },
  horeca: {
    label: "HoReCa — Hotels, Restaurants, Catering",
    icon: UtensilsCrossed,
    images: {
      food: [IMG_HORECA],
      nonfood: [IMG_HORECA],
    },
    alt: "Chef plating a dish in a luxury restaurant kitchen",
    desc: "Procurement discipline for hospitality groups — without diluting the guest experience.",
    food: [
      "Menu-cost engineering for hotel F&B and restaurant groups",
      "Central kitchen & catering supplier consolidation",
      "Wine, spirits & specialty sourcing through EU cellars",
      "Demand forecasting that cuts fresh waste double digits",
    ],
    nonfood: [
      "OS&E procurement frameworks for hotel openings",
      "Tableware, linen & amenity supplier vetting",
      "Sustainable packaging for delivery & banqueting",
      "Multi-property contract harmonisation",
    ],
  },
};

const CATEGORIES = [
  { id: "food", label: "Food" },
  { id: "nonfood", label: "Non-Food" },
];

export default function Sectors() {
  const [sector, setSector] = useState("supermarkets");
  const [category, setCategory] = useState("food");
  const current = SECTORS[sector];
  const Icon = current.icon;

  return (
    <section
      id="sectors"
      data-testid="sectors-section"
      className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28"
    >
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
      >
        <motion.div variants={fadeUp} className="mb-4 flex items-center gap-4">
          <span className="h-px w-12 bg-gold" />
          <span className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-forest">
            Sectors
          </span>
        </motion.div>
        <motion.h2
          variants={fadeUp}
          className="max-w-2xl font-serif text-2xl font-medium tracking-tight text-zinc-900 sm:text-3xl lg:text-4xl"
        >
          Two worlds. Food and non-food.
        </motion.h2>
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mt-12 flex flex-col gap-4"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="sector-toggle">
          {Object.entries(SECTORS).map(([id, s]) => {
            const SIcon = s.icon;
            const isActive = sector === id;
            return (
              <button
                key={id}
                data-testid={`sector-tab-${id}`}
                onClick={() => setSector(id)}
                className={`flex items-center gap-4 border px-6 py-5 text-left transition-all duration-400 ${
                  isActive
                    ? "border-forest bg-forest text-alabaster shadow-lg"
                    : "border-line bg-white text-zinc-800 hover:border-gold"
                }`}
              >
                <SIcon className={`h-6 w-6 ${isActive ? "text-gold" : "text-forest"}`} />
                <span className="font-serif text-lg font-semibold sm:text-xl">{s.label}</span>
              </button>
            );
          })}
        </div>

        <div className="flex gap-2" data-testid="category-filter">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`category-chip-${c.id}`}
              onClick={() => setCategory(c.id)}
              className={`rounded-full border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${
                category === c.id
                  ? "border-gold bg-gold text-white"
                  : "border-line bg-white text-zinc-500 hover:border-gold hover:text-forest"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${sector}-${category}`}
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.55, ease: EASE }}
          className="mt-10 grid grid-cols-1 items-stretch gap-8 lg:grid-cols-12 lg:gap-12"
        >
          <div className="relative lg:col-span-5">
            <div className="absolute -left-3 -top-3 h-full w-full border border-gold/50" aria-hidden />
            <div className="relative flex h-full min-h-[320px] flex-col overflow-hidden border border-line bg-white shadow-xl">
              {current.images[category].map((src, i) => (
                <div key={src} className="relative min-h-0 flex-1 overflow-hidden">
                  <img
                    src={src}
                    alt={current.alt}
                    data-testid={i === 0 ? "sector-image" : `sector-image-${i}`}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
              ))}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,transparent_50%,rgba(18,19,18,0.4)_100%)]"
              />
            </div>
          </div>

          <div className="flex flex-col justify-center lg:col-span-7">
            <div className="flex items-center gap-3">
              <Icon className="h-5 w-5 text-gold" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                {current.label} — {category === "food" ? "Food" : "Non-Food"}
              </span>
            </div>
            <h3 className="mt-4 font-serif text-2xl font-semibold text-zinc-900 sm:text-3xl">
              {category === "food" ? "Food & beverage, engineered." : "Non-food, disciplined."}
            </h3>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-zinc-600">{current.desc}</p>
            <ul className="mt-8 space-y-4" data-testid="sector-insights-list">
              {current[category].map((insight, i) => (
                <motion.li
                  key={insight}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: EASE }}
                  className="flex items-start gap-4 border-b border-line pb-4 text-sm text-zinc-700 sm:text-base"
                >
                  <span className="mt-1 font-mono text-[10px] tracking-[0.2em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {insight}
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
